import { assertUnreachable } from '@shared/helpers/function.helpers';
import { isRowDiscarded } from '@shared/helpers/candidate-discard.helpers';
import {
  draftToOriginMapping,
  getImportRowDraft,
} from '@shared/helpers/import-row-draft.helpers';
import { candidateUnitFrom } from '@shared/helpers/materialize-candidate.helpers';
import { rowWithActiveIfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import type {
  CandidateRow,
  ElementOriginMapping,
  MappingReferenceType,
  ResourceCodeMapping,
  ResourceCodeMappingEntry,
} from '@shared/models/element-import.model';
import type { Baseline, IElement } from '@shared/models/project.model';
import type { ImportedElementsByRowId } from './candidate-status.helpers';
import type { MappingsByCode } from './element-imports.store';
import { resolveBulkActionTargetIds } from './list-multi-select.helpers';

/** Persist the current mapping, or overwrite one already stored for this code. */
export type SaveMappingAction = 'save' | 'update';

export const SAVE_MAPPING_ACTION_LABEL: Record<SaveMappingAction, string> = {
  save: 'Save mapping',
  update: 'Update mapping',
};

export const SAVE_MAPPING_SUCCESS_MESSAGE: Record<SaveMappingAction, string> = {
  save: 'Mapping saved',
  update: 'Mapping updated',
};

export const saveMappingActionLabel = (
  action: SaveMappingAction,
  targetCount: number,
): string =>
  targetCount > 1
    ? `${SAVE_MAPPING_ACTION_LABEL[action]} (${targetCount})`
    : SAVE_MAPPING_ACTION_LABEL[action];

export const saveMappingSuccessMessage = (
  action: SaveMappingAction,
  targetCount: number,
): string => {
  if (targetCount <= 1) return SAVE_MAPPING_SUCCESS_MESSAGE[action];
  return action === 'update'
    ? `${targetCount} mappings updated`
    : `${targetCount} mappings saved`;
};

/** Persist vs overwrite for a bulk run: Save if any row is new, else Update. */
export const saveMappingBatchAction = (
  actions: readonly SaveMappingAction[],
): SaveMappingAction => (actions.includes('save') ? 'save' : 'update');

const mappingEntryMatches = (
  a: ResourceCodeMappingEntry | undefined,
  b: ResourceCodeMappingEntry | undefined,
): boolean =>
  a?.unitTo === b?.unitTo &&
  a?.quantityFactor === b?.quantityFactor &&
  a?.quantityName === b?.quantityName;

const storedToOriginMapping = (
  stored: ResourceCodeMapping,
): ElementOriginMapping => ({
  referenceId: stored.referenceId,
  referenceType: stored.referenceType,
  mappings: stored.mappings,
});

/**
 * Mapping currently applied on an imported element. `stored` is the
 * resource-code row used when {@link ElementOrigin.source} is `'stored'`.
 */
export const mappingFromElementOrigin = (
  origin: IElement['origin'],
  stored: ResourceCodeMapping | undefined,
): ElementOriginMapping | undefined => {
  if (origin?.kind !== 'csvImport') return undefined;
  switch (origin.source) {
    case 'inline':
      return origin.mapping;
    case 'stored':
      return stored ? storedToOriginMapping(stored) : undefined;
    default:
      return assertUnreachable(origin);
  }
};

export type ResolveSaveMappingActionArgs = {
  isDiscarded: boolean;
  unitFrom: string | undefined;
  current: ElementOriginMapping | undefined;
  stored: ResourceCodeMapping | undefined;
};

/**
 * Hover/menu copy for promoting a row mapping to the resource-code table.
 * Returns `undefined` when the table already has this mapping (same skip flag,
 * product/recipe, and conversion for {@link ResolveSaveMappingActionArgs.unitFrom})
 * so the control stays hidden.
 */
export const resolveSaveMappingAction = ({
  isDiscarded,
  unitFrom,
  current,
  stored,
}: ResolveSaveMappingActionArgs): SaveMappingAction | undefined => {
  if (isDiscarded) {
    if (!stored) return 'save';
    if (stored.discard) return undefined;
    return 'update';
  }

  if (!current?.referenceId) return undefined;
  if (!stored) return 'save';
  if (stored.discard) return 'update';

  const sameTarget =
    stored.referenceId === current.referenceId &&
    stored.referenceType === current.referenceType;
  if (!sameTarget) return 'update';
  if (!unitFrom) return undefined;

  return mappingEntryMatches(
    stored.mappings[unitFrom],
    current.mappings[unitFrom],
  )
    ? undefined
    : 'update';
};

export type SaveMappingSnapshot = {
  row: CandidateRow;
  action: SaveMappingAction;
  resourceCode: string;
  unitFrom: string;
  referenceId: string | undefined;
  referenceType: MappingReferenceType | undefined;
  entry: ResourceCodeMappingEntry;
  isDiscarded: boolean;
};

export type ResolveSaveMappingSnapshotArgs = {
  version: Baseline | undefined;
  imported: ImportedElementsByRowId;
  stored: ResourceCodeMapping | undefined;
};

/**
 * Persistable mapping for one row, or `undefined` when Save/Update mapping
 * should stay hidden (no resource code, nothing to persist, or already stored
 * and unchanged).
 */
export const resolveSaveMappingSnapshot = (
  row: CandidateRow,
  { version, imported, stored }: ResolveSaveMappingSnapshotArgs,
): SaveMappingSnapshot | undefined => {
  if (!row.resourceCode) return undefined;
  const isDiscarded = isRowDiscarded(version, row.id);
  const element = imported.get(row.id);
  const draft = getImportRowDraft(version, row.id);
  const effectiveRow = rowWithActiveIfcMeasure(row, draft.ifcMeasure);
  const unitFrom = candidateUnitFrom(effectiveRow);
  const originMapping = mappingFromElementOrigin(element?.origin, stored);
  const draftMapping = unitFrom
    ? draftToOriginMapping(draft, unitFrom)
    : undefined;
  const current = originMapping ?? draftMapping;
  const action = resolveSaveMappingAction({
    isDiscarded,
    unitFrom,
    current,
    stored,
  });
  if (!action) return undefined;
  if (!isDiscarded && !unitFrom) return undefined;
  const entry = unitFrom && current ? (current.mappings[unitFrom] ?? {}) : {};
  return {
    row,
    action,
    resourceCode: row.resourceCode,
    unitFrom: unitFrom ?? '',
    referenceId: current?.referenceId,
    referenceType: current?.referenceId ? current.referenceType : undefined,
    entry,
    isDiscarded,
  };
};

export type SelectSaveMappingTargetRowsArgs = {
  version: Baseline | undefined;
  imported: ImportedElementsByRowId;
  mappingsByCode: MappingsByCode;
};

/**
 * Rows whose Save/Update mapping an action on `sourceRow` should persist.
 * Same visible-selection fan-out as bulk AI mapping; rows with nothing to
 * persist are dropped. If visible ids are not reported yet, falls back to
 * the source row so a single-row Save still works.
 */
export const selectSaveMappingTargetRows = (
  sourceRow: CandidateRow,
  allRows: readonly CandidateRow[],
  selectedIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
  { version, imported, mappingsByCode }: SelectSaveMappingTargetRowsArgs,
): SaveMappingSnapshot[] => {
  const targetIds = new Set(
    resolveBulkActionTargetIds(sourceRow.id, selectedIds, visibleRowIds),
  );
  // Hover/menu can still fire before visible-row ids are reported; persist the
  // source row rather than no-op (unlike AI mapping, Save already existed).
  if (targetIds.size === 0) {
    const snapshot = resolveSaveMappingSnapshot(sourceRow, {
      version,
      imported,
      stored: sourceRow.resourceCode
        ? mappingsByCode[sourceRow.resourceCode]
        : undefined,
    });
    return snapshot ? [snapshot] : [];
  }
  const snapshots: SaveMappingSnapshot[] = [];
  for (const row of allRows) {
    if (!targetIds.has(row.id)) continue;
    const snapshot = resolveSaveMappingSnapshot(row, {
      version,
      imported,
      stored: row.resourceCode ? mappingsByCode[row.resourceCode] : undefined,
    });
    if (snapshot) snapshots.push(snapshot);
  }
  return snapshots;
};
